import { z } from 'zod'

// Enum schemas
const DonationMethodEnum = z.enum(['e-transfer', 'deposit', 'cheque', 'cash'])
const DonationStatusEnum = z.enum(['pending', 'verified'])
const DonationSourceEnum = z.enum(['manual', 'etransfer_auto', 'import'])

// Base validation schemas
const AmountSchema = z.number().positive('Amount must be positive')
const DateSchema = z.string().min(1, 'Date is required')
const ChurchIdSchema = z.string().min(1, 'Church ID is required')
const DonorIdSchema = z.string().min(1, 'Donor ID is required')
const ChurchIdNullableSchema = z
  .string()
  .min(1, 'Church ID is required')
  .nullable()
const DonorIdNullableSchema = z
  .string()
  .min(1, 'Donor ID is required')
  .nullable()
const NotesSchema = z.string().optional()
const ReceiptNumberSchema = z.string().min(1, 'Receipt number is required')
const CashConfirmedSchema = z.boolean().optional()
const CashCounterNameSchema = z.string().optional()
const IsRegistrationFeeSchema = z.boolean().optional()

// Email-related schemas (optional)
const EmailRawSchema = z.string().optional()
const EmailFromSchema = z.string().email().optional()
const EmailSubjectSchema = z.string().optional()
const EmailIdempotencyKeySchema = z.string().optional()
const SentToMemberSchema = z.boolean().optional()

// Create donation schema (for user input)
export const DonationCreateSchema = z.object({
  churchId: ChurchIdSchema,
  donorId: DonorIdSchema,
  amount: AmountSchema,
  date: DateSchema,
  donationRef: z.string(),
  notes: NotesSchema,
  method: DonationMethodEnum,
  cashConfirmed: CashConfirmedSchema,
  cashCounterName: CashCounterNameSchema,
  isRegistrationFee: IsRegistrationFeeSchema,
})

// Update donation schema (includes _id and status)
export const DonationUpdateSchema = DonationCreateSchema.extend({
  _id: z.string().min(1, 'ID is required'),
  status: DonationStatusEnum,
})

// Assignment schema for assigning member/church to auto e-transfer donations
export const DonationAssignmentSchema = z.object({
  donorId: DonorIdSchema,
  churchId: ChurchIdSchema,
})

// Verification schema (requires valid churchId and donorId)
export const DonationVerificationSchema = z.object({
  _id: z.string().min(1, 'ID is required'),
  churchId: ChurchIdSchema,
  donorId: DonorIdSchema,
  amount: AmountSchema,
  date: DateSchema,
  donationRef: z.string(),
  notes: NotesSchema,
  method: DonationMethodEnum,
  status: z.literal('verified'),
  isRegistrationFee: IsRegistrationFeeSchema,
})

// Full donation schema (complete response from server)
// This allows null values for churchId/donorId when source is etransfer_auto
export const DonationSchema = z.object({
  _id: z.string().min(1, 'ID is required'),
  churchId: ChurchIdNullableSchema,
  donorId: DonorIdNullableSchema,
  amount: AmountSchema,
  date: DateSchema,
  donationRef: z.string().optional(),
  notes: NotesSchema,
  method: DonationMethodEnum,
  status: DonationStatusEnum,
  receiptNumber: ReceiptNumberSchema,
  source: DonationSourceEnum,
  emailRaw: EmailRawSchema,
  emailFrom: EmailFromSchema,
  emailSubject: EmailSubjectSchema,
  emailIdempotencyKey: EmailIdempotencyKeySchema,
  sentToMember: SentToMemberSchema,
  sentToMemberBy: z.string().optional(),
  sentToMemberAt: z.string().optional(),
  createdBy: z.string().optional(),
  createdAt: z.string().optional(),
  verifiedBy: z.string().optional(),
  verifiedAt: z.string().optional(),
  updatedBy: z.string().optional(),
  updatedAt: z.string().optional(),
  deposited: z.boolean().optional(),
  depositDate: DateSchema.optional(),
  depositReceiptDrivePath: z.string().optional(),
  cashCountedBy: z.array(z.string()).optional(),
  cashConfirmed: CashConfirmedSchema,
  cashCounterName: CashCounterNameSchema,
  isRegistrationFee: IsRegistrationFeeSchema,
})

// Conditional validation for verification - ensures churchId and donorId are not null when verifying
export const DonationVerificationValidationSchema = DonationSchema.refine(
  (data) => {
    if (data.status === 'verified') {
      return data.churchId !== null && data.donorId !== null
    }
    return true
  },
  {
    message: 'Church ID and Donor ID must be assigned before verification',
    path: ['churchId', 'donorId'],
  },
)

export const DepositReceiptUploadSchema = z.object({
  donationId: z.string().min(1, 'Donation ID is required'),
  depositDate: z.string().min(1, 'Deposit date is required'),
  depositReceipt: z
    .any()
    .refine((file) => !!file, { message: 'Deposit receipt file is required' }),
  notes: NotesSchema.optional(),
})

// Type exports
export type DonationCreateData = z.infer<typeof DonationCreateSchema>
export type DonationUpdateData = z.infer<typeof DonationUpdateSchema>
export type DonationAssignmentData = z.infer<typeof DonationAssignmentSchema>
export type DonationVerificationData = z.infer<
  typeof DonationVerificationSchema
>
export type DonationData = z.infer<typeof DonationSchema>
export type DonationVerificationValidationData = z.infer<
  typeof DonationVerificationValidationSchema
>
export type DepositReceiptUploadData = z.infer<
  typeof DepositReceiptUploadSchema
>
